import React, { useState } from 'react';
import { Box, CheckboxV2, Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import { Info } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTranslation } from '@shared/core';
import { useListingsPanelTelemetry } from '@apps/stays/AccommodationsConsole/components/ListingsPanel/ListingsPanel.telemetry';
import { CASH_FUND_GOLD } from './cashFund.constants';

type CashFundFilterToggleProps = Readonly<{
  checked: boolean;
  onChange: (checked: boolean) => void;
}>;

// "Save more, travel further" toggle at the top of the cash-fund filters sidebar (Figma 53294:10898).
// When on, the result set is filtered to stays the fund fully covers (out-of-pocket $0). The info icon
// mirrors the "Show available hotels first" tooltip (hover + click-to-toggle so it also works on touch).
export const CashFundFilterToggle = ({ checked, onChange }: CashFundFilterToggleProps) => {
  const { tq } = useTranslation('accommodationsConsole');
  const telemetry = useListingsPanelTelemetry();
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);

  const handleChange = (nextChecked: boolean) => {
    // Mirror the other filter-change events (e.g. showAvailableOnlyFilterChanged).
    telemetry.cashFundCoveredFilterChanged({ action: nextChecked ? 'added' : 'removed' });
    onChange(nextChecked);
  };

  const handleInfoClick = useEventCallback((e: React.MouseEvent) => {
    // Keep the icon out of the surrounding <label> so clicking it doesn't toggle the checkbox.
    e.preventDefault();
    e.stopPropagation();
    setTooltipIsOpen(prev => !prev);
  });

  return (
    <Flex
      alignItems="center"
      columnGap={pxToRem(3)}
    >
      <Flex
        as="label"
        alignItems="center"
        columnGap={3}
        cursor="pointer"
      >
        <CheckboxV2
          checked={checked}
          onChange={event => handleChange(event.target.checked)}
        />
        <TextV2
          typographyVariant="hed1"
          fontWeight={600}
          color={CASH_FUND_GOLD}
        >
          {tq`Save more, travel further`}
        </TextV2>
      </Flex>
      <Box
        as="button"
        type="button"
        onClick={handleInfoClick}
        display="inline-flex"
        aria-label={tq`About this filter`}
        __css={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: CASH_FUND_GOLD }}
      >
        <TooltipV2
          triggerType="hover"
          // `undefined` delegates to TooltipV2's internal hover state; `true` forces it open (click-to-toggle on touch).
          isOpen={tooltipIsOpen || undefined}
          onClose={() => setTooltipIsOpen(false)}
          overrides={{
            Content: { props: { paddingY: 4, paddingLeft: pxToRem(14), paddingRight: 4, borderRadius: pxToRem(12), color: 'white', marginLeft: 2 } },
          }}
          content={
            <Box
              maxWidth={pxToRem(230)}
              typographyVariant="label2"
              textAlign="left"
            >
              {tq`Only show stays your honeymoon fund fully covers — no out-of-pocket cost.`}
            </Box>
          }
          placement="top"
        >
          <Info size={16} />
        </TooltipV2>
      </Box>
    </Flex>
  );
};
